import { useCallback, useEffect, useMemo, useState } from 'react'
import { Loader2, LogOut, Inbox, CheckCircle2, Clock } from 'lucide-react'
import { apiGet, cachedGet } from '../../lib/api.js'
import { apiErrorText } from '../../lib/apiError.js'
import { usePersistedState } from '../../lib/persist.js'
import { fullName } from '../../lib/employee.js'
import { nameInitial } from '../../lib/text.js'
import { EmployeeTrigger } from '../../components/employee/EmployeeDrawer.jsx'
import { resignLabel, resignTone, noticeGiven } from '../../lib/resignation.js'
import { fmtDay, fmtStamp } from '../../lib/warning.js'
import ResignationDrawer from '../../components/employee/ResignationDrawer.jsx'
import EmployeePicker from '../../components/employee/EmployeePicker.jsx'
import { useEmployeeFilter } from '../../components/employee/EmployeeFilter.jsx'

const FILTERS = [
  { key: 'pending', label: 'รอพิจารณา' },
  { key: 'accepted', label: 'รับทราบแล้ว' },
  { key: 'withdrawn', label: 'ถอนแล้ว' },
  { key: '', label: 'ทั้งหมด' },
]

/**
 * พนักงาน › เอกสาร › ลาออก — the resignation queue.
 *
 * Why this exists: people were simply not turning up, closing the LINE chat and
 * leaving, so the clinic never learned why and never did an off-boarding
 * conversation. This is the channel that makes leaving a thing you DO rather than
 * a thing you stop doing.
 *
 * 🔒 Manager/GM see everything on this screen. What they do NOT see is the
 * off-boarding questionnaire — only `survey_done`, a boolean. The answers are
 * behind /api/hr/resignation-survey/:id, HR/Admin only, enforced in the middleware
 * allowlist AND in Xano. That asymmetry is the feature: an honest answer about a
 * manager will not be written unless the person believes that manager cannot read
 * it.
 */
export default function EmployeeResignation() {
  const [status, setStatus] = usePersistedState('emp:resign:status', 'pending')
  // เอกสาร's own person, shared by its three tabs only — see EmployeeFilter.jsx.
  const [employeeId, setEmployeeId] = useEmployeeFilter('docs')
  const [rows, setRows] = useState([])
  const [employees, setEmployees] = useState([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [open, setOpen] = useState(null)

  useEffect(() => {
    cachedGet('/api/hr/employees', { per_page: 500 })
      .then((r) => setEmployees(Array.isArray(r) ? r : (r?.items || []))).catch(() => {})
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    setErr('')
    try {
      const r = await apiGet('/api/hr/resignation', status ? { status } : undefined)
      setRows(Array.isArray(r?.items) ? r.items : [])
    } catch (e) {
      setErr(apiErrorText(e, 'โหลดรายการลาออกไม่สำเร็จ'))
    } finally {
      setLoading(false)
    }
  }, [status])

  useEffect(() => { load() }, [load])

  const empById = useMemo(() => {
    const m = new Map()
    for (const e of employees) m.set(Number(e.id), e)
    return m
  }, [employees])

  const visible = useMemo(
    () => (employeeId ? rows.filter((r) => String(r.employee_id) === String(employeeId)) : rows),
    [rows, employeeId],
  )

  return (
    <div>
      <div className="flex flex-wrap items-center gap-1 mb-4">
        {FILTERS.map((f) => (
          <button
            key={f.key || 'all'}
            type="button"
            onClick={() => setStatus(f.key)}
            className={`px-2.5 py-1.5 rounded-lg text-[13px] transition ${
              status === f.key
                ? 'bg-brand-50 text-brand-700 ring-1 ring-brand-100 font-medium'
                : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900'
            }`}
          >
            {f.label}
          </button>
        ))}
        {/* activeOnly={false}: once their last day passes the person is inactive, and
            their letter is still on this register. */}
        <div className="ml-auto">
          <EmployeePicker value={employeeId} onChange={setEmployeeId} allowAll activeOnly={false} />
        </div>
      </div>

      {err && <p className="text-[13px] text-rose-600 mb-3">{err}</p>}

      {loading ? (
        <div className="py-16 text-center text-slate-400"><Loader2 size={20} className="animate-spin inline" /></div>
      ) : visible.length === 0 ? (
        // A failed load is not "nothing here" — the error above already says so.
        err ? null : <div className="py-16 text-center">
          <Inbox size={24} className="mx-auto text-slate-300 mb-2" />
          <p className="text-[13.5px] text-slate-400">
            {employeeId ? 'ไม่มีรายการของพนักงานที่เลือก' : status === 'pending' ? 'ไม่มีหนังสือแจ้งลาออกที่รอพิจารณา' : 'ไม่มีรายการ'}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-100">
          <table className="w-full text-[12.5px]">
            <thead>
              <tr className="text-[11px] text-slate-400 bg-slate-50/70 text-left">
                <th className="px-2.5 py-1.5 font-medium">พนักงาน</th>
                <th className="px-2.5 py-1.5 font-medium">แจ้งเมื่อ</th>
                <th className="px-2.5 py-1.5 font-medium">วันสุดท้าย</th>
                <th className="px-2.5 py-1.5 font-medium text-right">แจ้งล่วงหน้า</th>
                <th className="px-2.5 py-1.5 font-medium">แบบสอบถาม</th>
                <th className="px-2.5 py-1.5 font-medium">สถานะ</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => {
                const emp = empById.get(Number(r.employee_id))
                const notice = noticeGiven(r.filed_at || r.created_at, r.last_working_date)
                return (
                  <tr
                    key={r.id}
                    onClick={() => setOpen(r)}
                    className="border-t border-slate-50 hover:bg-brand-50/30 cursor-pointer"
                  >
                    {/* The row opens the RESIGNATION; the person cell opens the person. */}
                    <td className="px-2.5 py-2">
                      <EmployeeTrigger employeeId={r.employee_id} className="items-center gap-2">
                        <span className="w-7 h-7 rounded-lg bg-slate-100 text-slate-500 text-[11.5px] font-semibold flex items-center justify-center shrink-0 ring-1 ring-slate-200">
                          {nameInitial(emp?.nickname || emp?.name || '?')}
                        </span>
                        <div className="min-w-0">
                          <div className="font-semibold text-slate-800 truncate">{emp?.code || `#${r.employee_id}`}</div>
                          <div className="text-[11px] text-slate-400 truncate">
                            {emp ? fullName(emp) : ''}{emp?.position ? ` · ${emp.position}` : ''}
                          </div>
                        </div>
                      </EmployeeTrigger>
                    </td>
                    <td className="px-2.5 py-2 whitespace-nowrap tabular-nums text-slate-600">
                      {fmtStamp(r.filed_at || r.created_at)}
                    </td>
                    <td className="px-2.5 py-2 whitespace-nowrap">
                      <span className="tabular-nums text-slate-800">{fmtDay(r.last_working_date)}</span>
                      {/* 🚨 "ที่ขอ" until Employee.date_end actually says the same
                          thing. Two unlabelled answers to "when did they leave" is
                          exactly the confusion this label exists to prevent. */}
                      {!r.applied_to_employee && (
                        <span className="ml-1 text-[10.5px] text-amber-600">ที่ขอ</span>
                      )}
                    </td>
                    <td className="px-2.5 py-2 text-right tabular-nums">
                      {notice == null ? '—' : <span className={notice < 30 ? 'text-amber-600' : 'text-slate-600'}>{notice} วัน</span>}
                    </td>
                    <td className="px-2.5 py-2 whitespace-nowrap">
                      {r.survey_done
                        ? <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 size={12} /> ตอบแล้ว</span>
                        : <span className="inline-flex items-center gap-1 text-slate-400"><Clock size={12} /> ยังไม่ตอบ</span>}
                    </td>
                    <td className="px-2.5 py-2">
                      <span className={`px-2 py-0.5 rounded-lg text-[11.5px] font-medium ring-1 whitespace-nowrap ${resignTone(r.status)}`}>
                        {resignLabel(r.status)}
                      </span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="mt-3 flex items-start gap-1.5 text-[11.5px] text-slate-400">
        <LogOut size={12} className="shrink-0 mt-0.5" />
        คำตอบในแบบสอบถามก่อนพ้นสภาพเปิดดูได้เฉพาะฝ่ายบุคคล — หน้านี้แสดงเพียงว่าตอบแล้วหรือยัง
      </p>

      {open && (
        <ResignationDrawer
          resignation={open}
          employee={empById.get(Number(open.employee_id))}
          onClose={() => setOpen(null)}
          onSaved={() => { setOpen(null); load() }}
        />
      )}
    </div>
  )
}
